import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FileUp, Loader2, Paperclip, PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/_components/ui'
import { FILE_TYPE_LABELS } from '@/features/_constants/person-receipt'
import { mutations } from '@/features/_lib/react-query/mutations'
import type { TAttachmentType } from '@doutorsim/shared/exposed/application-attachments'

type CreateAttachmentButtonProps = {
  applicationId: string
  existingFilenames: string[]
  userType: 'staff' | 'tenant'
}

const TENANT_DEFAULT_TYPE: TAttachmentType = 'disbursement-receipt'

export const CreateAttachmentButton = ({
  applicationId,
  existingFilenames,
  userType,
}: CreateAttachmentButtonProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [fileType, setFileType] = useState<TAttachmentType | null>(
    userType === 'tenant' ? TENANT_DEFAULT_TYPE : null,
  )

  const queryClient = useQueryClient()
  const uploadMutation = useMutation(mutations.attachments.upload(queryClient))

  const resetState = () => {
    setFile(null)
    setFileType(userType === 'tenant' ? TENANT_DEFAULT_TYPE : null)
    setIsDragOver(false)
  }

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open)
    if (!open) resetState()
  }

  const validateFile = (file: File) => {
    const VALID_TYPES = ['image/png', 'image/jpeg', 'application/pdf']
    if (!VALID_TYPES.includes(file.type)) {
      toast.error('O arquivo deve ser um possuir um formato válido')
      return false
    }

    if (file.size > 10000000) {
      toast.error('O arquivo é muito grande, o tamanho máximo é de 10MB')
      return false
    }

    if (existingFilenames.includes(file.name)) {
      toast.error('Já existe um anexo com este nome nesta solicitação')
      return false
    }

    return true
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => setIsDragOver(false)

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragOver(false)
    const droppedFile = event.dataTransfer.files[0]
    if (!droppedFile) return
    if (!validateFile(droppedFile)) return
    setFile(droppedFile)
  }

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return
    if (!validateFile(selectedFile)) return
    setFile(selectedFile)
    event.target.value = ''
  }

  const handleUpload = () => {
    if (!file || !fileType) return

    uploadMutation.mutate(
      { applicationId, file, type: fileType },
      {
        onSuccess: () => {
          toast.success('Arquivo enviado com sucesso')
          resetState()
          setIsDialogOpen(false)
        },
        onError: () => {
          toast.error('Erro ao enviar o arquivo')
        },
      },
    )
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="xs" className="flex items-center gap-1 font-bold text-brand-grayscale-1">
          <PlusIcon size={16} />
          {userType === 'tenant' ? 'Anexar Nota Fiscal' : 'Novo Anexo'}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="min-w-[336px] max-w-[540px]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogTitle>{userType === 'tenant' ? 'Anexar Nota Fiscal' : 'Novo Anexo'}</DialogTitle>

        <div
          className={`flex w-full items-center justify-center rounded-lg border-[1px] border-dashed p-10 ${isDragOver ? 'border-brand-green-primary-7 bg-brand-green-primary-1' : 'border-brand-grayscale-5'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <label
            htmlFor="attachment-file"
            className="flex w-full cursor-pointer flex-col items-center justify-center gap-3 text-sm"
          >
            <FileUp className="size-10 text-brand-grayscale-7" />
            {file ? (
              <span className="w-full truncate text-center text-sm font-bold text-brand-green-primary-5">
                {file.name}
              </span>
            ) : (
              <>
                <p className="font-semibold">Selecione o arquivo ou arraste e solte aqui</p>
                <p className="font-semibold text-brand-grayscale-7">
                  JPG, PNG ou PDF, arquivos menores que 10MB
                </p>
                <span className="flex gap-1 text-sm font-bold text-brand-green-primary-5">
                  Selecione o arquivo <Paperclip size={20} />
                </span>
              </>
            )}
            <input
              type="file"
              accept="image/png, image/jpeg, .pdf"
              id="attachment-file"
              className="hidden"
              onChange={handleOnChange}
            />
          </label>
        </div>

        {userType === 'staff' && (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold">Tipo do documento</label>
            <Select
              value={fileType ?? undefined}
              onValueChange={(value) => setFileType(value as TAttachmentType)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(FILE_TYPE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="gray" className="font-bold" disabled={uploadMutation.isPending}>
              Cancelar
            </Button>
          </DialogClose>
          <Button disabled={!file || !fileType || uploadMutation.isPending} onClick={handleUpload}>
            {uploadMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
